/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Sparkles, Camera, Upload, Loader2, Info, X } from 'lucide-react';

// Components
import NailShapeSelector from '../../components/nailsStudio/NailShapeSelector';
import NailLengthSelector from '../../components/nailsStudio/NailLengthSelector';
import NailColorSelector from '../../components/nailsStudio/NailColorSelector';
import NailFinishSelector from '../../components/nailsStudio/NailFinishSelector';
import BeforeAfterViewer from '../../components/haircutsStudio/BeforeAfterViewer';
import ClientSearchModal from '../../components/ClientSearchModal';
import CameraCapture from '../../components/haircutsStudio/CameraCapture';
import axiosClient from '../../api/axiosClient';

// Data & Utils
import { 
  nailShapes, 
  nailLengths, 
  nailColors, 
  nailFinishes,
  getNailShapeById,
  getNailLengthById,
  getNailColorById,
  getNailFinishById
} from '../../data/nailsStudioData';
import { buildNailsPrompt } from '../../utils/nailsStudioPromptBuilder';
import { validateImageFile } from '../../services/hairstyleApi';
import {
  clearNailsStudioSession,
  dataUrlToFile,
  loadNailsStudioSession,
  saveNailsStudioSession,
} from '../../utils/haircutsStudioSession';

// Brand accent color
const ACCENT = '#7d6633';
const MOBILE_VIEWPORT_QUERY = '(max-width: 767px)';

const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const useIsMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_VIEWPORT_QUERY).matches : false
  ));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener?.('change', updateViewport);
    return () => mediaQuery.removeEventListener?.('change', updateViewport);
  }, []);

  return isMobile;
};

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900/60 dark:backdrop-blur-xl ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-zinc-200 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

const NailsStudio = () => {
  const { user } = useAdminAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation('admin');
  const fileInputRef = useRef(null);
  const resultCardRef = useRef(null);
  const sessionSaveWarningShownRef = useRef(false);
  const generationPhaseTimersRef = useRef([]);
  const isMobileViewport = useIsMobileViewport();

  // ===== STATE =====
  // Nail Options
  const [selectedShapeId, setSelectedShapeId] = useState('square');
  const [selectedLengthId, setSelectedLengthId] = useState('medium');
  const [selectedColorId, setSelectedColorId] = useState('natural');
  const [selectedFinishId, setSelectedFinishId] = useState('glossy');

  // User Custom Prompt
  const [userCustomPrompt, setUserCustomPrompt] = useState('');

  // Client & Image
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [showClientRequiredModal, setShowClientRequiredModal] = useState(false);
  const [sourceImage, setSourceImage] = useState(null);
  const [sourceImageFile, setSourceImageFile] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null); // Scan record for AI Analysis linkage

  // Mode Detection (manicure or pedicure)
  const [detectedMode, setDetectedMode] = useState(null); // Will be set after MediaPipe detection

  // Generation
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [usedPrompt, setUsedPrompt] = useState('');
  const [generationPhase, setGenerationPhase] = useState(0);
  const [sessionHydrated, setSessionHydrated] = useState(false);

  // Generate validation popup
  const [showGenerateValidationPopup, setShowGenerateValidationPopup] = useState(false);
  const [generateValidationMessage, setGenerateValidationMessage] = useState('');

  // ===== HANDLERS =====
  const handleReset = () => {
    setSelectedShapeId('square');
    setSelectedLengthId('medium');
    setSelectedColorId('natural');
    setSelectedFinishId('glossy');
    setUserCustomPrompt('');
    setGeneratedImage(null);
    setUsedPrompt('');
    toast.success('Selections reset');
  };

  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const savedSession = loadNailsStudioSession();
      if (!savedSession) {
        setSessionHydrated(true);
        return;
      }

      setSelectedClient(savedSession.selectedClient || null);
      setSelectedShapeId(savedSession.selectedShapeId || 'square');
      setSelectedLengthId(savedSession.selectedLengthId || 'medium');
      setSelectedColorId(savedSession.selectedColorId || 'natural');
      setSelectedFinishId(savedSession.selectedFinishId || 'glossy');
      setUserCustomPrompt(savedSession.userCustomPrompt || '');
      setSourceImage(savedSession.sourceImage || null);
      setSelectedScan(savedSession.selectedScan || null);
      setDetectedMode(savedSession.detectedMode || null);
      setGeneratedImage(savedSession.generatedImage || null);
      setUsedPrompt(savedSession.usedPrompt || '');

      if (savedSession.sourceImage) {
        const restoredFile = await dataUrlToFile(
          savedSession.sourceImage,
          savedSession.sourceImageFileName || 'nails-studio-photo.jpg'
        );
        if (!cancelled) setSourceImageFile(restoredFile);
      }

      if (!cancelled) setSessionHydrated(true);
    };

    restoreSession();

    return () => {
      cancelled = true;
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!sessionHydrated) return;

    const hasSessionData =
      !!selectedClient ||
      !!sourceImage ||
      !!selectedScan ||
      !!generatedImage ||
      !!userCustomPrompt.trim() ||
      selectedShapeId !== 'square' ||
      selectedLengthId !== 'medium' ||
      selectedColorId !== 'natural' ||
      selectedFinishId !== 'glossy' ||
      !!detectedMode;

    if (!hasSessionData) {
      clearNailsStudioSession();
      return;
    }

    const didSave = saveNailsStudioSession({
      selectedClient,
      selectedShapeId,
      selectedLengthId,
      selectedColorId,
      selectedFinishId,
      userCustomPrompt,
      sourceImage,
      sourceImageFileName: sourceImageFile?.name || null,
      selectedScan,
      detectedMode,
      generatedImage,
      usedPrompt,
    });

    if (!didSave && !sessionSaveWarningShownRef.current) {
      sessionSaveWarningShownRef.current = true;
      toast.error(t('admin.haircuts_session_save_failed'));
    }
  }, [
    detectedMode,
    generatedImage,
    selectedClient,
    selectedColorId,
    selectedFinishId,
    selectedLengthId,
    selectedScan,
    selectedShapeId,
    sessionHydrated,
    sourceImage,
    sourceImageFile,
    t,
    usedPrompt,
    userCustomPrompt,
  ]);

  const focusGeneratedResultOnMobile = () => {
    if (!isMobileViewport) return;
    resultCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setShowClientSearch(false);
    toast.success(`Selected client: ${client.name}`);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if client is selected
    if (!selectedClient) {
      setShowClientRequiredModal(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Create a Scan record (same pipeline as Haircuts/Makeup so AI Analysis can see it)
    try {
      const uploadToast = toast.loading('Uploading client image...');
      const form = new FormData();
      form.append('client_name', selectedClient.name || 'Client');
      form.append('salon_id', selectedClient.shop_id || 3);
      form.append('barber_id', user?.id || '');
      form.append('image', file); // must be "image" to match /api/scans/upload

      const resp = await axiosClient.post('/scans/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (resp.data?.scan) {
        setSelectedScan(resp.data.scan);
      }
      toast.success('Client image uploaded!', { id: uploadToast });
    } catch (err) {
      console.error('Scan upload failed:', err);
      toast.error('Upload failed (AI Analysis may not persist).');
      setSelectedScan(null);
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setSourceImage(e.target.result);
      setSourceImageFile(file);
      toast.success('Image uploaded! Detecting hands/feet...');
      
      // Detect mode (manicure vs pedicure) using MediaPipe
      detectMode(file);
    };
    reader.readAsDataURL(file);

    // Clear file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const detectMode = async (file) => {
    try {
      // Call MediaPipe detection API
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/nails/detect-mode', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDetectedMode(data.mode); // 'manicure' or 'pedicure'
        toast.success(`Detected: ${data.mode === 'manicure' ? 'Hands (Manicure)' : 'Feet (Pedicure)'}`);
      } else {
        // Log error for debugging
        console.error('Mode detection failed:', response.status, response.statusText);
        // Fallback to manicure if detection fails
        setDetectedMode('manicure');
        toast('Defaulting to manicure mode');
      }
    } catch (error) {
      console.error('Mode detection error:', error);
      setDetectedMode('manicure');
      toast('Defaulting to manicure mode');
    }
  };

  const handleGenerateClick = async () => {
    // Validation
    if (!selectedClient) {
      toast.error('Please select a client first');
      setShowClientSearch(true);
      return;
    }

    if (!sourceImageFile) {
      toast.error('Please upload an image first');
      return;
    }

    if (!detectedMode) {
      toast.error('Please wait for mode detection to complete');
      return;
    }

    if (generating) {
      return; // Prevent repeated clicks
    }

    // Must select at least one option
    if (!selectedShapeId && !selectedColorId && !selectedFinishId && !userCustomPrompt.trim()) {
      setGenerateValidationMessage("Please select at least one nail option (shape, color, or finish) before generating.");
      setShowGenerateValidationPopup(true);
      return;
    }

    // Build prompt
    const selectedShape = getNailShapeById(selectedShapeId);
    const selectedLength = getNailLengthById(selectedLengthId);
    const selectedColor = getNailColorById(selectedColorId);
    const selectedFinish = getNailFinishById(selectedFinishId);

    const prompt = buildNailsPrompt({
      mode: detectedMode,
      shape: selectedShape?.promptText,
      length: detectedMode === 'manicure' ? selectedLength?.promptText : null, // Only for manicure
      color: selectedColor?.promptText,
      finish: selectedFinish?.promptText,
      customPrompt: userCustomPrompt.trim()
    });

    setUsedPrompt(prompt);

    // Generate
    await generateNails(prompt);
  };

  const generateNails = async (prompt) => {
    generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
    generationPhaseTimersRef.current = [];
    setGenerationPhase(1);
    setGenerating(true);
    setGeneratedImage(null);

    const loadingToast = toast.loading('Generating your nail design...');

    try {
      if (isMobileViewport) {
        await waitForNextPaint();
        focusGeneratedResultOnMobile();
      }

      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(2), 1500));
      generationPhaseTimersRef.current.push(window.setTimeout(() => setGenerationPhase(3), 4000));

      // Call AI generation API
      const formData = new FormData();
      // Prefer scan-based generation so results persist and appear in AI Analysis
      if (selectedScan?.id) formData.append('scanId', String(selectedScan.id));
      else formData.append('file', sourceImageFile);
      formData.append('prompt', prompt);
      formData.append('mode', detectedMode);
      // Persist nail option metadata for analysis/details
      formData.append('shape', selectedShapeId);
      formData.append('length', selectedLengthId);
      formData.append('color', selectedColorId);
      formData.append('finish', selectedFinishId);

      const response = await fetch('/api/nails/generate', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Generation failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      setGenerationPhase(4);

      if (result.output) {
        if (isMobileViewport) {
          await new Promise(resolve => window.setTimeout(resolve, 700));
        }
        // If backend returns a relative "/uploads/..." path, render it from backend origin
        const out = String(result.output);
        const absolute = out.startsWith('/') ? new URL(out, window.location.origin).toString() : out;
        setGeneratedImage(absolute);
        toast.success('Nail design generated successfully!', { id: loadingToast });
        window.dispatchEvent(new Event('credits:changed'));
      } else {
        throw new Error('No output received from AI service');
      }
    } catch (error) {
      generationPhaseTimersRef.current.forEach(id => window.clearTimeout(id));
      generationPhaseTimersRef.current = [];
      console.error('Generation error:', error);
      toast.error(error.message || 'Failed to generate nail design', { id: loadingToast });
    } finally {
      setGenerationPhase(0);
      setGenerating(false);
    }
  };

  const handleDownload = async (imageUrl) => {
    try {
      toast.loading('Downloading image...');
      
      // Fetch the image as blob
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      
      // Create object URL from blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `nails-${selectedClient?.name?.replace(/\s+/g, '-') || 'result'}-${Date.now()}.jpg`;
      a.style.display = 'none';
      
      // Add to document, click, and remove
      document.body.appendChild(a);
      a.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.dismiss();
      toast.success('Image downloaded successfully!');
      
    } catch (error) {
      toast.dismiss();
      toast.error('Download failed. Please try again or right-click the image to save.');
    }
  };

  return (
    <div className="relative min-h-screen pb-32 md:pb-6">
      {/* Brand Glass Morphism Background Elements - Hidden in light mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden dark:block">
        <div className="absolute top-10 left-10 w-96 h-96 bg-gradient-to-br from-[#7d6633]/25 to-[#5a4d26]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-32 right-16 w-80 h-80 bg-gradient-to-br from-[#a0824a]/20 to-[#7d6633]/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-gradient-to-br from-[#d4b47a]/15 to-[#a0824a]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 right-20 w-64 h-64 bg-gradient-to-br from-[#7d6633]/30 to-[#d4b47a]/15 rounded-full blur-3xl"></div>
      </div>

      <div className="relative space-y-6 z-10">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
                <Sparkles className="h-6 w-6 mr-2" style={{ color: ACCENT }} />
                {t('admin.nails_studio_title')}
              </h1>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                {t('admin.nails_studio_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Mode Detection Badge */}
        {detectedMode && (
          <div className="hidden md:flex items-center justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#7d6633]/10 border border-[#7d6633]/30">
              <Sparkles className="h-4 w-4 text-[#7d6633]" />
              <span className="text-sm font-medium text-[#7d6633] dark:text-[#a0824a]">
                Mode: {detectedMode === 'manicure' ? 'Manicure (Hands)' : 'Pedicure (Feet)'}
              </span>
            </div>
          </div>
        )}

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* LEFT PANEL - Nail Options */}
          <div className="lg:col-span-3 space-y-4 order-2 lg:order-1">
            
            {/* Nail Shape */}
            <Card>
              <CardHeader
                title="Nail Shape"
                subtitle="Select shape"
                icon={<div className="text-lg">💅</div>}
              />
              <div className="p-4">
                <NailShapeSelector
                  shapes={nailShapes}
                  selectedShapeId={selectedShapeId}
                  onShapeSelect={setSelectedShapeId}
                  disabled={generating}
                />
              </div>
            </Card>

            {/* Nail Length (only for manicure) */}
            {detectedMode === 'manicure' && (
              <Card>
                <CardHeader
                  title="Nail Length"
                  subtitle="Select length"
                  icon={<div className="text-lg">📏</div>}
                />
                <div className="p-4">
                  <NailLengthSelector
                    lengths={nailLengths}
                    selectedLengthId={selectedLengthId}
                    onLengthSelect={setSelectedLengthId}
                    disabled={generating}
                  />
                </div>
              </Card>
            )}

            {/* Nail Color */}
            <Card>
              <CardHeader
                title="Nail Color"
                subtitle="Select color"
                icon={<div className="text-lg">🎨</div>}
              />
              <div className="p-4">
                <NailColorSelector
                  colors={nailColors}
                  selectedColorId={selectedColorId}
                  onColorSelect={setSelectedColorId}
                  disabled={generating}
                />
              </div>
            </Card>

            {/* Nail Finish */}
            <Card>
              <CardHeader
                title="Nail Finish"
                subtitle="Select finish"
                icon={<div className="text-lg">✨</div>}
              />
              <div className="p-4">
                <NailFinishSelector
                  finishes={nailFinishes}
                  selectedFinishId={selectedFinishId}
                  onFinishSelect={setSelectedFinishId}
                  disabled={generating}
                />
              </div>
            </Card>

            {/* Reset Button */}
            <Card>
              <div className="p-4">
                <motion.button
                  onClick={handleReset}
                  disabled={generating}
                  className="w-full py-2.5 px-4 rounded-lg border-2 border-zinc-300 dark:border-zinc-600
                           bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300
                           hover:bg-zinc-50 dark:hover:bg-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200 text-sm font-medium
                           flex items-center justify-center gap-2"
                  whileHover={!generating ? { scale: 1.01 } : {}}
                  whileTap={!generating ? { scale: 0.99 } : {}}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {t('admin.reset_selections')}
                </motion.button>
              </div>
            </Card>

          </div>

          {/* CENTER PANEL - Before/After Viewer */}
          <div className="lg:col-span-6 space-y-4 order-3 lg:order-2 flex flex-col">
            
            {/* Generated Result Display */}
            <div ref={resultCardRef} className="order-1 lg:order-1">
              <Card>
              <CardHeader
                title="Generated Result"
                subtitle={generatedImage ? 'Your new nail design preview' : 'Results will appear here'}
                icon={<Sparkles className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4">
                <BeforeAfterViewer
                  beforeImage={sourceImage}
                  afterImage={generatedImage}
                  loading={generating}
                  onDownload={handleDownload}
                  generationPhase={generationPhase}
                  loadingStepLabels={[
                    t('admin.mobile_generation_upload_received'),
                    t('admin.mobile_generation_analyzing_image'),
                    t('admin.mobile_generation_applying_selected_style'),
                    t('admin.mobile_generation_rendering_result'),
                  ]}
                />
              </div>
              </Card>
            </div>

            {/* Generate Button (PRIMARY CTA) */}
            <Card className="hidden md:block order-2">
              <div className="p-4">
                <motion.button
                  onClick={handleGenerateClick}
                  disabled={generating || !sourceImageFile || !detectedMode}
                  className={`
                    w-full py-3.5 px-6 rounded-lg font-semibold text-base
                    transition-all duration-200 flex items-center justify-center gap-2.5
                    ${generating || !sourceImageFile || !detectedMode
                      ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
                    }
                  `}
                  whileHover={!generating && sourceImageFile && detectedMode ? { scale: 1.01 } : {}}
                  whileTap={!generating && sourceImageFile && detectedMode ? { scale: 0.99 } : {}}
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{t('admin.generating_nail_design')}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5" />
                      <span>{t('admin.generate_nail_design')}</span>
                    </>
                  )}
                </motion.button>
                {!sourceImageFile && (
                  <p className="mt-2 text-xs text-center text-zinc-500 dark:text-zinc-400">
                    {t('admin.upload_image_first')}
                  </p>
                )}
                {sourceImageFile && !detectedMode && (
                  <p className="mt-2 text-xs text-center text-orange-600 dark:text-orange-400">
                    Detecting hands/feet...
                  </p>
                )}
              </div>
            </Card>

            {/* Custom Prompt Input */}
            <Card className="order-3 lg:order-3">
              <CardHeader
                title="Custom Request"
                subtitle="Add additional details (optional)"
                icon={<div className="text-lg">✍️</div>}
              />
              <div className="p-4">
                <textarea
                  value={userCustomPrompt}
                  onChange={(e) => setUserCustomPrompt(e.target.value)}
                  placeholder="Example: add small gold accent on ring finger, matte top coat..."
                  disabled={generating}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 
                           bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 text-sm
                           focus:outline-none focus:ring-2 focus:ring-[#7d6633] focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                />
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                  {t('admin.nail_custom_prompt_hint')}
                </p>
              </div>
            </Card>

            {/* Used Prompt Display */}
            {usedPrompt && (
              <Card className="order-4">
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-zinc-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 uppercase tracking-wide mb-1">
                        {t('admin.ai_prompt_used')}
                      </p>
                      <pre className="text-xs text-zinc-500 dark:text-zinc-400 whitespace-pre-wrap font-mono bg-zinc-50 dark:bg-zinc-800/50 p-3 rounded-lg">
                        {usedPrompt}
                      </pre>
                    </div>
                  </div>
                </div>
              </Card>
            )}

          </div>

          {/* RIGHT PANEL - Client Selection & Upload */}
          <div className="lg:col-span-3 space-y-4 order-1 lg:order-3">
            
            {/* Client Selection & Upload */}
            <Card>
              <CardHeader
                title="Client & Image"
                subtitle="Select client first"
                icon={<Camera className="h-5 w-5" style={{ color: ACCENT }} />}
              />
              <div className="p-4 space-y-4">
                {/* Client Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{t('admin.client_label')}</span>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowClientSearch(true)}
                      className="px-3 py-1.5 rounded-lg text-xs bg-[#7d6633] hover:bg-[#6d5b2e] text-white font-medium"
                    >
                      {selectedClient ? 'Change' : 'Select'}
                    </motion.button>
                  </div>
                  {selectedClient ? (
                    <div className="p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-white/10">
                      <div className="flex items-center gap-2">
                        <span className="text-[#7d6633]">👤</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{selectedClient.name}</p>
                          {selectedClient.email && (
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{selectedClient.email}</p>
                          )}
                        </div>
                        <span className="text-xs text-emerald-600">✅</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('admin.no_client_selected')}</p>
                  )}
                </div>

                {/* Upload Section */}
                <div className="border-2 border-dashed p-4 rounded-xl bg-zinc-50 dark:bg-zinc-800/30 text-center border-[#7d6633]/30">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="ns-file-upload"
                  />
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedClient) {
                          toast.error('Please select a client first');
                          setShowClientSearch(true);
                          return;
                        }
                        fileInputRef.current?.click();
                      }}
                      disabled={generating}
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                        !selectedClient
                          ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
                          : 'bg-[#7d6633] hover:bg-[#6d5b2e] text-white'
                      }`}
                    >
                      <Upload className="h-4 w-4" />
                      {t('admin.upload_photo')}
                    </button>

                    <CameraCapture
                      onFileSelect={handleFileSelect}
                      disabled={generating || !selectedClient}
                      className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg w-full sm:w-auto ${
                        !selectedClient
                          ? 'bg-zinc-300 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 cursor-not-allowed'
                          : 'bg-zinc-900 hover:bg-zinc-800 text-white'
                      }`}
                      buttonLabel="Take Photo"
                      facingMode="user"
                    />
                  </div>
                  <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">{t('admin.image_format_hint')}</p>
                  {!selectedClient && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">⚠️ Select client first</p>
                  )}
                </div>

                {/* Source Image Thumbnail */}
                {sourceImage && (
                  <div className="relative aspect-square rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-white/10">
                    <img
                      src={sourceImage}
                      alt="Source"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/60 backdrop-blur-sm">
                      <span className="text-xs text-white font-medium">{t('admin.label_original')}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card>

          </div>

        </div>

      </div>

      <div
        className="md:hidden fixed inset-x-0 z-30 px-4 pointer-events-none"
        style={{ bottom: 'calc(112px + env(safe-area-inset-bottom, 0px))' }}
      >
        <motion.button
          type="button"
          onClick={handleGenerateClick}
          disabled={generating}
          className="pointer-events-auto w-full rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 px-4 py-3.5 text-sm font-semibold text-white shadow-xl transition-all hover:from-pink-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-60 flex items-center justify-center gap-2"
          whileTap={{ scale: 0.98 }}
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('admin.generating_nail_design')}</span>
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              <span>{t('admin.generate_nail_design')}</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Client Search Modal */}
      <ClientSearchModal
        isOpen={showClientSearch}
        onClose={() => setShowClientSearch(false)}
        onClientSelect={handleClientSelect}
        isDark={isDark}
      />

      {/* Client Required Modal */}
      {showClientRequiredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="max-w-md w-full"
          >
            <Card>
              <div className="p-6 md:p-8">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 rounded-full bg-[#7d6633]/10 flex items-center justify-center">
                    <Info className="h-8 w-8 text-[#7d6633]" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center mb-2 text-zinc-900 dark:text-zinc-100">
                  {t('admin.client_selection_required')}
                </h3>

                {/* Message */}
                <p className="text-center text-zinc-600 dark:text-zinc-400 mb-6">
                  Please select a client before uploading an image. This helps us track and save the results to the correct client profile.
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowClientRequiredModal(false);
                      setShowClientSearch(true);
                    }}
                    className="flex-1 px-4 py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg bg-[#7d6633] hover:bg-[#6d5b2e]"
                  >
                    {t('admin.select_client')}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowClientRequiredModal(false)}
                    className="flex-1 px-4 py-3 rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-800/50 text-zinc-700 dark:text-zinc-300 font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition-all"
                  >
                    {t('admin.cancel')}
                  </motion.button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Generate Validation Popup */}
      {showGenerateValidationPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="max-w-md w-full"
          >
            <Card>
              <div className="p-6 md:p-7">
                <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                  {t('admin.action_required')}
                </h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {generateValidationMessage}
                </p>
                <div className="mt-5 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowGenerateValidationPopup(false)}
                    className="px-4 py-2 rounded-lg bg-[#7d6633] hover:bg-[#6d5b2e] text-white text-sm font-semibold transition-colors"
                  >
                    OK
                  </motion.button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default NailsStudio;
